import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Users, Briefcase, Search, BarChart3, DollarSign, FileText, Send, CheckCircle, XCircle, Eye, TrendingUp, Mail, Target, Radio, Shield, Calendar, Trophy, Settings, Linkedin, Download, ExternalLink, Trash2, BookOpen } from 'lucide-react';
import { ListBuilderTab, OutreachQueueTab, OutreachTrackerTab, PostSubmissionsTab, TemplateEditorTab } from '../components/AdminOutreach';
import { ExecutiveDatabaseTab, CampaignBuilderTab, DashboardStatsCards } from '../components/AdminCampaign';
import { VerificationTab, AICostsTab, SLADashboard } from '../components/AdminVerification';
import { AdminCalendarTab, ReferralLeaderboardTab } from '../components/AdminCalendarLeaderboard';
import { AdminContentLibraryTab } from '../components/ContentLibrary';
import { AdminEngagementsTab } from '../components/EngagementComponents';
import CleanupTab from '../components/AdminCleanup';
import AdminServiceOrders from '../components/AdminServiceOrders';
import AdminBrandsManager from '../components/AdminBrandsManager';
import AdminProposals from '../components/AdminProposals';
import AdminAttributionTab from '../components/AdminAttribution';
import ConversionsSetupDoc from '../components/ConversionsSetupDoc';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function TabNav({ active, setActive }) {
  const tabs = [
    { key: 'signups', label: 'Signups', icon: Users },
    { key: 'exec-db', label: 'Exec Database', icon: Users },
    { key: 'campaign-builder', label: 'Campaign Builder', icon: Briefcase },
    { key: 'engagements', label: 'Engagements', icon: Users },
    { key: 'review', label: 'Exec Review', icon: Users },
    { key: 'campaigns', label: 'Campaigns', icon: Briefcase },
    { key: 'list-builder', label: 'List Builder', icon: Search },
    { key: 'outreach-queue', label: 'Queue', icon: Send },
    { key: 'outreach-tracker', label: 'Tracker', icon: Target },
    { key: 'templates', label: 'Templates', icon: FileText },
    { key: 'submissions', label: 'Submissions', icon: FileText },
    { key: 'verification', label: 'Verification', icon: Shield },
    { key: 'sla', label: 'SLA', icon: BarChart3 },
    { key: 'calendar', label: 'Calendar', icon: Calendar },
    { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { key: 'content-lib', label: 'Content', icon: FileText },
    { key: 'payouts', label: 'Payouts', icon: DollarSign },
    { key: 'brands', label: 'Brands (Concierge)', icon: Users },
    { key: 'proposals', label: 'Proposals', icon: FileText },
    { key: 'service-orders', label: 'Service Orders', icon: DollarSign },
    { key: 'ai-costs', label: 'AI Costs', icon: BarChart3 },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'attribution', label: 'Traffic / UTMs', icon: TrendingUp },
    { key: 'cleanup', label: 'Cleanup', icon: Trash2 },
    { key: 'conversions-doc', label: 'GA4 / Meta Setup', icon: BookOpen },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];
  return (
    <div className="flex overflow-x-auto border-b border-white/10 mb-8 gap-0.5 scrollbar-hide -mx-6 px-6 sm:mx-0 sm:px-0">
      {tabs.map(t => (
        <button key={t.key} onClick={() => setActive(t.key)} data-testid={`admin-tab-${t.key}`}
          className={`flex items-center gap-1.5 px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${
            active === t.key ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'
          }`}>
          <t.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 hidden sm:block" /> {t.label}
        </button>
      ))}
    </div>
  );
}

// ==================== Signups (Contact Outreach) Tab ====================
function SignupsTab() {
  const [data, setData] = useState({ executives: [], brands: [], totals: {} });
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeList, setActiveList] = useState('executives');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/signups`, { headers: getAuthHeaders(), params: search ? { search } : {} })
        .then(r => setData(r.data || { executives: [], brands: [], totals: {} })).catch(() => {}),
      axios.get(`${API}/admin/brand-inquiries`, { headers: getAuthHeaders() })
        .then(r => setInquiries(r.data?.inquiries || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadCSV = async (role) => {
    try {
      const res = await axios.get(`${API}/admin/signups/export.csv`, {
        headers: getAuthHeaders(), params: { role }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `freddyleads_${role}s.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('CSV export failed'); }
  };

  const t = data.totals || {};
  const list = activeList === 'executives' ? data.executives : activeList === 'brands' ? data.brands : inquiries;

  return (
    <div className="space-y-5" data-testid="admin-signups-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Signups <span className="text-[#FF4343]">Contact Outreach</span></h2>
          <p className="text-[10px] text-[#6B7280] mt-1">Every brand + executive who has signed up — with contact info and CSV export.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadCSV('executive')} data-testid="export-execs-csv"
            className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[#E03030]">
            <Download className="w-3.5 h-3.5" /> Executives CSV
          </button>
          <button onClick={() => downloadCSV('brand')} data-testid="export-brands-csv"
            className="border-2 border-[#FF4343] bg-transparent text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Brands CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {[
          { label: 'Executives', val: t.executives },
          { label: 'Approved', val: t.executives_approved, color: 'text-[#10B981]' },
          { label: 'Pending', val: t.executives_pending, color: 'text-yellow-400' },
          { label: 'Brands', val: t.brands },
          { label: 'With Campaigns', val: t.brands_with_campaigns, color: 'text-[#10B981]' },
          { label: 'Paying', val: t.brands_paying, color: 'text-[#FF4343]' },
          { label: 'Inquiries', val: inquiries.length, color: 'text-[#FF4343]' },
        ].map((s, i) => (
          <div key={i} className="bg-[#111827] border border-white/10 p-3">
            <div className={`text-xl font-mono ${s.color || 'text-white'}`}>{s.val ?? 0}</div>
            <div className="text-[10px] text-[#6B7280] font-mono uppercase">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="Search by email, name, or company..." data-testid="signups-search"
          className="flex-1 px-3 py-2 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        <button onClick={load} className="border border-white/20 text-[#9CA3AF] hover:text-white px-4 py-2 text-xs">Search</button>
      </div>

      {/* Tab toggle */}
      <div className="flex gap-1 border-b border-white/10">
        {[{ k: 'executives', l: `Executives (${data.executives.length})` }, { k: 'brands', l: `Brands (${data.brands.length})` }, { k: 'inquiries', l: `Inquiries (${inquiries.length})` }].map(t => (
          <button key={t.k} onClick={() => setActiveList(t.k)} data-testid={`signups-tab-${t.k}`}
            className={`px-4 py-2 text-xs border-b-2 ${activeList === t.k ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}>{t.l}</button>
        ))}
      </div>

      {/* Table */}
      {loading ? <p className="text-[#9CA3AF]">Loading…</p>
        : list.length === 0 ? <p className="text-[#9CA3AF] text-center py-8">No {activeList} yet.</p>
          : (
            <div className="overflow-x-auto bg-[#111827] border border-white/10">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-white/5 text-[10px] uppercase font-mono text-[#6B7280]">
                    {activeList === 'executives' ? (
                      <><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Company / Title</th><th className="px-3 py-2 text-left">Industries</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Pitch?</th><th className="px-3 py-2 text-left">Signed Up</th><th className="px-3 py-2"></th></>
                    ) : activeList === 'inquiries' ? (
                      <><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Company</th><th className="px-3 py-2 text-left">Message</th><th className="px-3 py-2 text-left">Budget</th><th className="px-3 py-2 text-left">Source</th><th className="px-3 py-2 text-left">Date</th></>
                    ) : (
                      <><th className="px-3 py-2 text-left">Brand</th><th className="px-3 py-2 text-left">Portal Email</th><th className="px-3 py-2 text-left">Industry</th><th className="px-3 py-2 text-left">Campaigns</th><th className="px-3 py-2 text-left">Paying?</th><th className="px-3 py-2 text-left">Website</th><th className="px-3 py-2 text-left">Signed Up</th></>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {list.map((row, i) => activeList === 'executives' ? (
                    <tr key={row.id || i} className="border-t border-white/5 hover:bg-white/5" data-testid={`signup-row-exec-${i}`}>
                      <td className="px-3 py-2 text-white font-semibold">{row.name}</td>
                      <td className="px-3 py-2"><a href={`mailto:${row.email}`} className="text-[#FF4343] hover:underline">{row.email}</a></td>
                      <td className="px-3 py-2 text-[#9CA3AF]">{row.headline || '—'}<br /><span className="text-[10px] text-[#6B7280]">{row.current_company}</span></td>
                      <td className="px-3 py-2 text-[#9CA3AF]">{(row.industries || []).slice(0, 2).join(', ') || '—'}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-mono uppercase ${row.status === 'approved' ? 'text-[#10B981]' : row.status === 'pending' ? 'text-yellow-400' : 'text-[#9CA3AF]'}`}>{row.status}</span></td>
                      <td className="px-3 py-2 text-[#9CA3AF]">{row.open_to_sales_pitch ? '✓' : '—'}</td>
                      <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(row.created_at || '').slice(0, 10)}</td>
                      <td className="px-3 py-2">{row.linkedin_url && <a href={row.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-[#FF4343]"><ExternalLink className="w-3 h-3" /></a>}</td>
                    </tr>
                  ) : activeList === 'inquiries' ? (
                    <tr key={row.inquiry_id || i} className="border-t border-white/5 hover:bg-white/5" data-testid={`signup-row-inquiry-${i}`}>
                      <td className="px-3 py-2 text-white font-semibold">{row.name}</td>
                      <td className="px-3 py-2"><a href={`mailto:${row.email}`} className="text-[#FF4343] hover:underline">{row.email}</a></td>
                      <td className="px-3 py-2 text-[#9CA3AF]">{row.company || '—'}</td>
                      <td className="px-3 py-2 text-[#9CA3AF] max-w-[280px]"><span title={row.message}>{(row.message || '—').slice(0, 90)}{(row.message || '').length > 90 ? '…' : ''}</span></td>
                      <td className="px-3 py-2 text-[#9CA3AF]">{row.budget || '—'}</td>
                      <td className="px-3 py-2 text-[10px] font-mono text-[#9CA3AF]">{row.attribution?.utm_source || 'direct'}</td>
                      <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(row.created_at || '').slice(0, 10)}</td>
                    </tr>
                  ) : (
                    <tr key={row.id || i} className="border-t border-white/5 hover:bg-white/5" data-testid={`signup-row-brand-${i}`}>
                      <td className="px-3 py-2 text-white font-semibold">{row.name}</td>
                      <td className="px-3 py-2"><a href={`mailto:${row.portal_email}`} className="text-[#FF4343] hover:underline">{row.portal_email}</a></td>
                      <td className="px-3 py-2 text-[#9CA3AF]">{row.industry || '—'}</td>
                      <td className="px-3 py-2 text-white font-mono">{row.campaign_count}</td>
                      <td className="px-3 py-2">{row.has_payment_method ? <span className="text-[#10B981]">✓</span> : <span className="text-[#6B7280]">—</span>}</td>
                      <td className="px-3 py-2">{row.website ? <a href={row.website} target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-[#FF4343] inline-flex items-center gap-1">{row.website.replace(/^https?:\/\//, '').slice(0, 25)} <ExternalLink className="w-3 h-3" /></a> : '—'}</td>
                      <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(row.created_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );
}

function ExecReviewTab() {
  const [execs, setExecs] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchExecs(); }, [filter]);

  const fetchExecs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/executives/users?status=${filter}`, { headers: getAuthHeaders() });
      setExecs(data);
    } catch {} finally { setLoading(false); }
  };

  const updateStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/executives/${userId}/status`, { status }, { headers: getAuthHeaders() });
      fetchExecs();
    } catch {}
  };

  return (
    <div data-testid="admin-exec-review">
      <div className="flex gap-3 mb-6">
        {['pending', 'approved', 'suspended'].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`exec-filter-${s}`}
            className={`text-xs font-mono uppercase tracking-wider px-4 py-2 border transition-colors ${
              filter === s ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF] hover:text-white'
            }`}>{s}</button>
        ))}
      </div>
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : execs.length === 0 ? <p className="text-[#9CA3AF]">No executives in this status.</p> : (
        <div className="space-y-3">
          {execs.map(ex => (
            <div key={ex.user_id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between" data-testid={`exec-row-${ex.user_id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#1F2937] overflow-hidden flex-shrink-0">
                  {ex.headshot_url ? <img src={ex.headshot_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-[#6B7280] m-auto mt-2.5" />}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-white">{ex.name}</h3>
                  <p className="text-xs text-[#6B7280]">{ex.headline} &middot; {ex.industries?.join(', ')} &middot; {ex.linkedin_connections || 0} connections</p>
                </div>
              </div>
              <div className="flex gap-2">
                {filter === 'pending' && (
                  <>
                    <button onClick={() => updateStatus(ex.user_id, 'approved')} data-testid={`approve-${ex.user_id}`}
                      className="flex items-center gap-1 px-3 py-1 text-xs border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors">
                      <CheckCircle className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => updateStatus(ex.user_id, 'suspended')} data-testid={`reject-${ex.user_id}`}
                      className="flex items-center gap-1 px-3 py-1 text-xs border border-red-400 text-red-400 hover:bg-red-400 hover:text-white transition-colors">
                      <XCircle className="w-3 h-3" /> Reject
                    </button>
                  </>
                )}
                {filter === 'approved' && (
                  <span className="text-xs font-mono text-[#10B981] flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> Active</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/campaigns?status=`, { headers: getAuthHeaders() }).then(r => setCampaigns(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleStatus = async (campId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/campaigns/${campId}`, { status: newStatus }, { headers: getAuthHeaders() });
      setCampaigns(campaigns.map(c => c.id === campId ? { ...c, status: newStatus } : c));
    } catch {}
  };

  return (
    <div data-testid="admin-campaigns">
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between" data-testid={`admin-camp-${c.id}`}>
              <div>
                <h3 className="text-sm font-medium text-white">{c.title}</h3>
                <p className="text-xs text-[#6B7280]">{c.brand_name} &middot; {c.industry} &middot; {c.spots_filled}/{c.spots_total} spots</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-mono uppercase ${c.status === 'active' ? 'text-[#10B981]' : 'text-[#FF4343]'}`}>{c.status}</span>
                <button onClick={() => toggleStatus(c.id, c.status)} data-testid={`toggle-camp-${c.id}`}
                  className="text-xs px-3 py-1 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">
                  {c.status === 'active' ? 'Pause' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProspectingTab() {
  const [searchType, setSearchType] = useState('executive');
  const [criteria, setCriteria] = useState({ industry: '', title_keywords: '', min_connections: 500, geography: '', bio_keywords: '' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pitchResult, setPitchResult] = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/prospecting/search`, { type: searchType, ...criteria }, { headers: getAuthHeaders() });
      setResults(data);
    } catch {} finally { setLoading(false); }
  };

  const handleGeneratePitch = async () => {
    setPitchLoading(true);
    try {
      const { data } = await axios.post(`${API}/prospecting/pitch-email`, {
        brand_info: { company: criteria.title_keywords || 'Target Company', name: 'Contact', title: 'VP Marketing', industry: criteria.industry }
      }, { headers: getAuthHeaders() });
      setPitchResult(data);
    } catch {} finally { setPitchLoading(false); }
  };

  return (
    <div data-testid="admin-prospecting">
      <div className="flex gap-3 mb-6">
        {['executive', 'brand'].map(t => (
          <button key={t} onClick={() => setSearchType(t)} data-testid={`prospect-type-${t}`}
            className={`text-xs font-mono uppercase tracking-wider px-4 py-2 border transition-colors ${
              searchType === t ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF] hover:text-white'
            }`}>{t} Prospecting</button>
        ))}
      </div>

      <div className="bg-[#111827] border border-white/10 p-6 mb-6">
        <h3 className="text-sm font-semibold mb-4">Search Criteria</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Industry</label>
            <select value={criteria.industry} onChange={e => setCriteria({...criteria, industry: e.target.value})} data-testid="prospect-industry"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              <option value="">Any</option>
              {['Automotive', 'Real Estate', 'Medical', 'Tech', 'Finance', 'Legal'].map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Title Keywords</label>
            <input value={criteria.title_keywords} onChange={e => setCriteria({...criteria, title_keywords: e.target.value})} placeholder="e.g., dealer principal, CMO" data-testid="prospect-titles"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Min Connections</label>
            <input type="number" value={criteria.min_connections} onChange={e => setCriteria({...criteria, min_connections: parseInt(e.target.value) || 0})} data-testid="prospect-min-conn"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Geography</label>
            <input value={criteria.geography} onChange={e => setCriteria({...criteria, geography: e.target.value})} placeholder="e.g., Southeast US" data-testid="prospect-geo"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={handleSearch} disabled={loading} data-testid="prospect-search-btn"
            className="flex items-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-5 py-2 text-sm font-semibold transition-all duration-300 disabled:opacity-50">
            <Search className="w-4 h-4" /> {loading ? 'Searching...' : 'Generate Search Strings'}
          </button>
          {searchType === 'brand' && (
            <button onClick={handleGeneratePitch} disabled={pitchLoading} data-testid="generate-pitch-btn"
              className="flex items-center gap-2 border border-white/20 text-white hover:bg-white/5 px-5 py-2 text-sm font-medium transition-all disabled:opacity-50">
              <Mail className="w-4 h-4" /> {pitchLoading ? 'Generating...' : 'Generate Pitch Email'}
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-[#111827] border border-white/10 p-6 space-y-4" data-testid="prospect-results">
          <h3 className="text-sm font-semibold">Search Strings</h3>
          {results.linkedin_search && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">LinkedIn Sales Navigator</label>
              <div className="bg-[#090E33] border border-white/10 p-3 text-sm text-[#9CA3AF] font-mono break-all">{results.linkedin_search}</div>
            </div>
          )}
          {results.apollo_search && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Apollo.io</label>
              <div className="bg-[#090E33] border border-white/10 p-3 text-sm text-[#9CA3AF] font-mono break-all">{results.apollo_search}</div>
            </div>
          )}
          {results.tips?.length > 0 && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Tips</label>
              <ul className="space-y-1">
                {results.tips.map((t, i) => <li key={i} className="text-sm text-[#9CA3AF]">- {t}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {pitchResult && (
        <div className="bg-[#111827] border border-white/10 p-6 mt-4 space-y-4" data-testid="pitch-result">
          <h3 className="text-sm font-semibold">Generated Pitch</h3>
          {pitchResult.subject_lines && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Subject Lines</label>
              {pitchResult.subject_lines.map((s, i) => <p key={i} className="text-sm text-[#FF4343]">{i + 1}. {s}</p>)}
            </div>
          )}
          {pitchResult.email_body && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Email Body</label>
              <div className="bg-[#090E33] border border-white/10 p-4 text-sm text-[#9CA3AF] whitespace-pre-wrap">{pitchResult.email_body}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CaseStudiesTab() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/case-studies`, { headers: getAuthHeaders() }).then(r => setStudies(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const deleteStudy = async (id) => {
    try {
      await axios.delete(`${API}/case-studies/${id}`, { headers: getAuthHeaders() });
      setStudies(studies.filter(s => s.id !== id));
    } catch {}
  };

  return (
    <div data-testid="admin-case-studies">
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : (
        <div className="space-y-3">
          {studies.map(cs => (
            <div key={cs.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between" data-testid={`admin-cs-${cs.id}`}>
              <div>
                <h3 className="text-sm font-medium text-white">{cs.industry} - {cs.campaign_type}</h3>
                <p className="text-xs text-[#6B7280]">
                  <span className="font-mono">${cs.total_spend?.toLocaleString()}</span> spend &middot;
                  <span className="font-mono"> {(cs.total_impressions / 1000).toFixed(0)}K</span> impressions &middot;
                  <span className="font-mono text-[#FF4343]"> ${cs.cpm}</span> CPM
                </p>
              </div>
              <div className="flex gap-2">
                {cs.is_featured && <span className="text-xs font-mono text-[#FF4343]">Featured</span>}
                <button onClick={() => deleteStudy(cs.id)} data-testid={`delete-cs-${cs.id}`}
                  className="text-xs px-3 py-1 border border-red-400/50 text-red-400 hover:bg-red-400 hover:text-white transition-colors">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PayoutsTab() {
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [completedApps, setCompletedApps] = useState([]);
  const [connectAccounts, setConnectAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [payoutsRes, txnRes, appsRes, connectRes] = await Promise.all([
        axios.get(`${API}/admin/payouts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/payments/transactions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/applications?status=completed`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/connect/accounts`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setPayouts(payoutsRes.data);
      setTransactions(txnRes.data);
      setConnectAccounts(connectRes.data || []);
      const paidAppIds = new Set(payoutsRes.data.map(p => p.application_id));
      setCompletedApps((appsRes.data || []).filter(a => a.status === 'completed' && a.payout_status !== 'paid' && !paidAppIds.has(a.id)));
    } catch {} finally { setLoading(false); }
  };

  const triggerPayout = async (appId) => {
    try {
      await axios.post(`${API}/admin/payouts/${appId}`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Payout failed');
    }
  };

  const completePayout = async (payoutId) => {
    try {
      await axios.put(`${API}/admin/payouts/${payoutId}/complete`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch {}
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading payouts...</p>;

  return (
    <div data-testid="admin-payouts" className="space-y-8">
      {/* Pending Payouts */}
      {completedApps.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-4 text-white">Completed Campaigns - Pending Payout</h3>
          <div className="space-y-2">
            {completedApps.map(app => (
              <div key={app.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between" data-testid={`pending-payout-${app.id}`}>
                <div>
                  <h4 className="text-sm text-white">{app.campaign_title}</h4>
                  <p className="text-xs text-[#6B7280]">{app.executive_name} &middot; {app.brand_name}</p>
                </div>
                <button onClick={() => triggerPayout(app.id)} data-testid={`trigger-payout-${app.id}`}
                  className="text-xs px-4 py-2 border-2 border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] transition-colors font-medium">
                  Trigger Payout
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Payouts */}
      <div>
        <h3 className="text-sm font-semibold mb-4 text-white">Payout History</h3>
        {payouts.length === 0 ? (
          <p className="text-[#6B7280] text-sm">No payouts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Executive</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Campaign</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Amount</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Status</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="py-3 text-white">{p.executive_name}</td>
                    <td className="py-3 text-[#9CA3AF]">{p.campaign_title}</td>
                    <td className="py-3 font-mono text-[#FF4343]">${p.amount?.toLocaleString()}</td>
                    <td className="py-3">
                      <span className={`font-mono text-xs uppercase ${p.status === 'paid' ? 'text-[#10B981]' : 'text-[#FF4343]'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-3">
                      {p.status === 'processing' && (
                        <button onClick={() => completePayout(p.id)} data-testid={`complete-payout-${p.id}`}
                          className="text-xs px-3 py-1 border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors">
                          Mark Paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Transactions */}
      {transactions.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-4 text-white">Payment Transactions</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Campaign</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Amount</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Status</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} className="border-b border-white/5">
                    <td className="py-3 text-white">{t.campaign_title}</td>
                    <td className="py-3 font-mono text-[#FF4343]">${t.amount?.toLocaleString()}</td>
                    <td className="py-3">
                      <span className={`font-mono text-xs uppercase ${t.payment_status === 'paid' ? 'text-[#10B981]' : t.payment_status === 'initiated' ? 'text-[#FF4343]' : 'text-[#6B7280]'}`}>
                        {t.payment_status}
                      </span>
                    </td>
                    <td className="py-3 text-[#6B7280] text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Connected Accounts */}
      {connectAccounts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-4 text-white">Stripe Connect Accounts</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="connect-accounts-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Executive</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Email</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Stripe ID</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Onboarding</th>
                  <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Payouts</th>
                </tr>
              </thead>
              <tbody>
                {connectAccounts.map(a => (
                  <tr key={a.user_id} className="border-b border-white/5">
                    <td className="py-3 text-white">{a.name}</td>
                    <td className="py-3 text-[#9CA3AF] text-xs">{a.email}</td>
                    <td className="py-3 font-mono text-xs text-[#6B7280]">{a.stripe_account_id}</td>
                    <td className="py-3">
                      <span className={`font-mono text-xs uppercase ${a.details_submitted ? 'text-[#10B981]' : 'text-[#FF4343]'}`}>
                        {a.details_submitted ? 'Complete' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider ${a.payouts_enabled ? 'text-[#10B981]' : 'text-[#6B7280]'}`}>
                        <span className={`w-2 h-2 rounded-full ${a.payouts_enabled ? 'bg-[#10B981] animate-pulse' : 'bg-gray-500'}`} />
                        {a.payouts_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, { headers: getAuthHeaders() }).then(r => setAnalytics(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-[#9CA3AF]">Loading analytics...</p>;
  if (!analytics) return <p className="text-[#9CA3AF]">Failed to load.</p>;

  const stats = [
    { label: 'Total Executives', value: analytics.total_executives, color: 'text-white' },
    { label: 'Approved', value: analytics.approved_executives, color: 'text-[#10B981]' },
    { label: 'Pending', value: analytics.pending_executives, color: 'text-[#FF4343]' },
    { label: 'Active Campaigns', value: analytics.active_campaigns, color: 'text-[#10B981]' },
    { label: 'Total Applications', value: analytics.total_applications, color: 'text-white' },
    { label: 'Completed', value: analytics.completed_applications, color: 'text-[#10B981]' },
    { label: 'Total Spend', value: `$${analytics.total_spend_processed?.toLocaleString()}`, color: 'text-[#FF4343]' },
    { label: 'Avg CPM', value: `$${analytics.avg_cpm}`, color: 'text-[#FF4343]' },
  ];

  return (
    <div data-testid="admin-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-[#111827] border border-white/10 p-4 sm:p-6 text-center min-w-0">
            <span className={`block font-mono text-xl sm:text-3xl font-bold tabular-nums leading-tight truncate ${s.color}`}>{s.value}</span>
            <p className="text-[10px] sm:text-xs text-[#6B7280] font-mono uppercase tracking-wider mt-2 truncate">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState('exec-db');

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#9CA3AF] mb-4">Admin access required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="admin-panel">
      <div className="border-b border-white/10 bg-[#111827]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-7 w-7 sm:h-8 sm:w-8" />
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold">Admin <span className="text-[#FF4343]">Panel</span></h1>
              <p className="text-[#9CA3AF] text-xs sm:text-sm mt-0.5 hidden sm:block">Freddy Media Internal Dashboard</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8">
        <TabNav active={tab} setActive={setTab} />
        <DashboardStatsCards />
        {tab === 'signups' && <SignupsTab />}
        {tab === 'exec-db' && <ExecutiveDatabaseTab />}
        {tab === 'campaign-builder' && <CampaignBuilderTab />}
        {tab === 'engagements' && <AdminEngagementsTab />}
        {tab === 'review' && <ExecReviewTab />}
        {tab === 'campaigns' && <CampaignsTab />}
        {tab === 'list-builder' && <ListBuilderTab />}
        {tab === 'outreach-queue' && <OutreachQueueTab />}
        {tab === 'outreach-tracker' && <OutreachTrackerTab />}
        {tab === 'templates' && <TemplateEditorTab />}
        {tab === 'submissions' && <PostSubmissionsTab />}
        {tab === 'verification' && <VerificationTab />}
        {tab === 'sla' && <SLADashboard />}
        {tab === 'calendar' && <AdminCalendarTab />}
        {tab === 'leaderboard' && <ReferralLeaderboardTab />}
        {tab === 'content-lib' && <AdminContentLibraryTab />}
        {tab === 'payouts' && <PayoutsTab />}
        {tab === 'brands' && <AdminBrandsManager />}
        {tab === 'proposals' && <AdminProposals />}
        {tab === 'service-orders' && <AdminServiceOrders />}
        {tab === 'ai-costs' && <AICostsTab />}
        {tab === 'analytics' && <AnalyticsTab />}
        {tab === 'attribution' && <AdminAttributionTab />}
        {tab === 'cleanup' && <CleanupTab />}
        {tab === 'conversions-doc' && <ConversionsSetupDoc />}
        {tab === 'settings' && <AdminSettingsTab />}
      </div>
    </div>
  );
}

function AdminSettingsTab() {
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [unipileStatus, setUnipileStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [pricingPrice, setPricingPrice] = useState(0.25);
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => {
    fetchLinkedinStatus();
    fetchPricing();
    fetchUnipileStatus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('linkedin') === 'connected') {
      fetchLinkedinStatus();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchLinkedinStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/linkedin/status`, { headers: getAuthHeaders() });
      setLinkedinStatus(data);
    } catch {} finally { setLoading(false); }
  };

  const fetchPricing = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/prospecting-pricing`, { headers: getAuthHeaders() });
      setPricingPrice(data.price_per_contact || 0.25);
    } catch {}
  };

  const fetchUnipileStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/unipile/status`, { headers: getAuthHeaders() });
      setUnipileStatus(data);
    } catch {}
  };

  const connectLinkedin = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/linkedin/connect`, { headers: getAuthHeaders() });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (e) { alert(e.response?.data?.detail || 'Failed to initiate LinkedIn connection'); }
  };

  const handlePost = async () => {
    if (!postText.trim()) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/admin/linkedin/post`, { commentary: postText }, { headers: getAuthHeaders() });
      alert(`Post created! URN: ${data.post_urn || 'success'}`);
      setPostText('');
    } catch (e) { alert(e.response?.data?.detail || 'Post failed'); }
    finally { setPosting(false); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/admin/prospecting-pricing`, { price_per_contact: pricingPrice }, { headers: getAuthHeaders() });
      alert('Pricing updated!');
    } catch (e) { alert('Failed to update'); }
    finally { setSavingPricing(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <h2 className="font-display text-2xl font-bold">Platform <span className="text-[#FF4343]">Settings</span></h2>

      {/* LinkedIn Integration */}
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-[#0A66C2] flex items-center justify-center">
            <Linkedin className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">LinkedIn Connection</h3>
            <p className="text-xs text-[#6B7280]">Connect your corporate LinkedIn to share campaigns and reach executives</p>
          </div>
        </div>

        {loading ? <p className="text-[#9CA3AF] text-sm">Loading...</p> : linkedinStatus?.connected ? (
          <div>
            <div className="flex items-center gap-3 mb-4 bg-[#10B981]/10 border border-[#10B981]/20 p-3">
              <CheckCircle className="w-5 h-5 text-[#10B981]" />
              <div>
                <p className="text-sm text-white font-medium">{linkedinStatus.name}</p>
                <p className="text-xs text-[#6B7280]">{linkedinStatus.email} &middot; Connected {new Date(linkedinStatus.connected_at).toLocaleDateString()}</p>
              </div>
              {linkedinStatus.picture && <img src={linkedinStatus.picture} alt="" className="w-8 h-8 rounded-full ml-auto" />}
            </div>
            <div>
              <label className="text-xs text-[#6B7280] uppercase block mb-1">Post to LinkedIn</label>
              <textarea value={postText} onChange={e => setPostText(e.target.value)} rows={3} placeholder="Write a post from your corporate LinkedIn account..." data-testid="linkedin-post-text"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#0A66C2] resize-none" />
              <button onClick={handlePost} disabled={posting || !postText.trim()} data-testid="linkedin-post-btn"
                className="mt-2 px-4 py-2 bg-[#0A66C2] text-white text-xs font-semibold hover:bg-[#094D92] transition-all disabled:opacity-50">
                {posting ? 'Posting...' : 'Post to LinkedIn'}
              </button>
            </div>
            <p className="text-[10px] text-[#6B7280] mt-4">
              <strong>LinkedIn DMs:</strong> {unipileStatus?.connected
                ? <span className="text-[#10B981]">Unipile connected — LinkedIn DMs enabled on offers.</span>
                : <span className="text-yellow-400">Connect Unipile for LinkedIn DM capability.</span>}
            </p>
          </div>
        ) : (
          <button onClick={connectLinkedin} data-testid="connect-linkedin-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2] text-white text-sm font-semibold hover:bg-[#094D92] transition-all">
            <Linkedin className="w-4 h-4" /> Connect LinkedIn Account
          </button>
        )}
      </div>

      {/* Unipile LinkedIn DMs */}
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-[#0A66C2] flex items-center justify-center">
            <Send className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">LinkedIn DMs (Unipile)</h3>
            <p className="text-xs text-[#6B7280]">Send campaign offers directly to executive LinkedIn inboxes</p>
          </div>
        </div>
        {unipileStatus?.connected ? (
          <div className="flex items-center gap-2 bg-[#10B981]/10 border border-[#10B981]/20 p-3">
            <CheckCircle className="w-5 h-5 text-[#10B981]" />
            <div>
              <p className="text-sm text-white font-medium">Connected</p>
              <p className="text-xs text-[#6B7280]">Account: Jon Frederick (LinkedIn) &middot; DM offers enabled</p>
            </div>
          </div>
        ) : (
          <div className="px-3 py-2 bg-white/5 border border-white/10 text-xs text-[#6B7280]">
            Status: <span className="text-yellow-400">Not connected</span> — Connect LinkedIn in Unipile dashboard
          </div>
        )}
      </div>

      {/* X (Twitter) Integration */}
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-black flex items-center justify-center border border-white/20">
            <span className="text-white font-bold text-sm">X</span>
          </div>
          <div>
            <h3 className="font-semibold text-white">X (Twitter) Connection</h3>
            <p className="text-xs text-[#6B7280]">Connect your X account to post campaigns and send DM offers</p>
          </div>
        </div>
        <p className="text-xs text-[#9CA3AF] mb-3">X integration requires a Developer App. Share your API keys when ready.</p>
        <div className="px-3 py-2 bg-white/5 border border-white/10 text-xs text-[#6B7280]">
          Status: <span className="text-yellow-400">Awaiting API credentials</span>
        </div>
      </div>

      {/* Prospecting Pricing */}
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
        <h3 className="font-semibold text-white mb-3">Apollo Prospecting Markup</h3>
        <p className="text-xs text-[#6B7280] mb-4">Set the per-contact price brands pay for executive searches. Your cost: ~$0.03/contact.</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-sm text-[#9CA3AF]">$</span>
            <input type="number" step="0.01" min="0.05" value={pricingPrice} onChange={e => setPricingPrice(parseFloat(e.target.value) || 0)} data-testid="pricing-input"
              className="w-24 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
            <span className="text-xs text-[#6B7280] ml-1">per contact</span>
          </div>
          <button onClick={savePricing} disabled={savingPricing} data-testid="save-pricing-btn"
            className="px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] text-xs font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
            {savingPricing ? 'Saving...' : 'Save'}
          </button>
          <span className="text-xs text-[#6B7280]">Margin: <span className="text-[#10B981]">{((pricingPrice - 0.03) / pricingPrice * 100).toFixed(0)}%</span></span>
        </div>
      </div>

      {/* Mailgun */}
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
        <h3 className="font-semibold text-white mb-3">Mailgun (Email)</h3>
        <p className="text-xs text-[#6B7280] mb-2">Status: API key configured. Domain DNS verification needed for delivery.</p>
        <div className="px-3 py-2 bg-white/5 border border-white/10 text-xs text-[#6B7280]">
          Domain: <span className="text-yellow-400">Needs DNS verification</span> — Add TXT/CNAME records at your domain registrar.
        </div>
      </div>

      {/* Twilio */}
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
        <h3 className="font-semibold text-white mb-3">Twilio (SMS)</h3>
        <p className="text-xs text-[#6B7280]">Status: <span className="text-[#10B981]">Configured</span> — SID and auth token active.</p>
      </div>
    </div>
  );
}
